import React, { useState, useCallback, useEffect } from 'react';
import { DrainDetectionProps, DrainDefect } from '@/types/chat';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpEvent,
  trackAdpWaypoint,
} from '@/services/analytics/adp';
import {
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  FileVideo,
  Clock,
  X,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize2,
  Ruler,
} from 'lucide-react';

// Strip unique suffix (timestamp + randomId) added during upload
// e.g. "video_1704825600000_a1b2c3.mp4" → "video.mp4"
const stripUploadSuffix = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  const ext = lastDotIndex > 0 ? filename.substring(lastDotIndex) : '';
  const name =
    lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
  const parts = name.split('_');
  if (parts.length < 3) return filename;
  // Remove last two parts (timestamp and randomId)
  return parts.slice(0, -2).join('_') + ext;
};

// Helper to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Helper to get frame number from image name
const getFrameNumber = (imageName: string): number => {
  const match = imageName.match(/frame(\d+)/i);
  return match ? parseInt(match[1], 10) : 0;
};

// Helper to get timestamp from image name
// Extracts timestamp in seconds from filename format: frame{section_number}_{timestamp_seconds}s.jpg
const getTimestampFromImageName = (imageName: string): number | undefined => {
  const match = imageName.match(/frame\d+_(\d+)s\./i);
  return match ? parseInt(match[1], 10) : undefined;
};

// Helper to get top defect (highest confidence that is not "None")
const getTopDefect = (defects: DrainDefect[]): DrainDefect | null => {
  const actualDefects = defects.filter(
    d => d.defect_code !== 'None' && d.confidence > 0,
  );
  if (actualDefects.length === 0) {
    // Return the "None" defect if no actual defects found
    return defects.find(d => d.defect_code === 'None') || null;
  }
  return actualDefects.reduce(
    (max, d) => (d.confidence > max.confidence ? d : max),
    actualDefects[0],
  );
};

// Helper to check if no defects detected
const hasNoDefects = (defects: DrainDefect[]): boolean => {
  const noneDefect = defects.find(d => d.defect_code === 'None');
  return (
    noneDefect !== null &&
    noneDefect !== undefined &&
    noneDefect.confidence >= 90
  );
};

// Helper to format video timestamp
const formatTimestamp = (timestamp: string | number | undefined): string => {
  if (timestamp === undefined || timestamp === null) return '';

  // If it's already in HH:MM:SS or MM:SS format, return as is
  if (typeof timestamp === 'string' && timestamp.includes(':')) {
    return timestamp;
  }

  // Convert seconds to MM:SS or HH:MM:SS format
  const totalSeconds =
    typeof timestamp === 'string' ? parseFloat(timestamp) : timestamp;

  if (isNaN(totalSeconds)) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  const isComplete = status.toLowerCase() === 'complete';
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
        isComplete
          ? 'bg-green-100 text-green-700'
          : 'bg-yellow-100 text-yellow-700'
      }`}>
      {isComplete && <CheckCircle className="h-3.5 w-3.5" />}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Image Lightbox component with zoom controls
const ImageLightbox = ({
  imageUrl,
  frameNumber,
  onClose,
}: {
  imageUrl: string;
  frameNumber: number;
  onClose: () => void;
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const MIN_SCALE = 0.5;
  const MAX_SCALE = 4;
  const SCALE_STEP = 0.5;

  const handleZoomIn = useCallback(() => {
    setScale(prev => {
      const next = Math.min(prev + SCALE_STEP, MAX_SCALE);
      trackAdpEvent(getBernoulliAdp(), ADP_EVENTS.DRAIN_DETECTION_ZOOM, {
        action: 'in',
        scale: String(Math.round(next * 100)),
        frame: String(frameNumber),
      });
      return next;
    });
  }, [frameNumber]);

  const handleZoomOut = useCallback(() => {
    setScale(prev => {
      const next = Math.max(prev - SCALE_STEP, MIN_SCALE);
      trackAdpEvent(getBernoulliAdp(), ADP_EVENTS.DRAIN_DETECTION_ZOOM, {
        action: 'out',
        scale: String(Math.round(next * 100)),
        frame: String(frameNumber),
      });
      return next;
    });
  }, [frameNumber]);

  const handleReset = useCallback(() => {
    trackAdpEvent(getBernoulliAdp(), ADP_EVENTS.DRAIN_DETECTION_ZOOM, {
      action: 'reset',
      scale: '100',
      frame: String(frameNumber),
    });
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [frameNumber]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -SCALE_STEP : SCALE_STEP;
    setScale(prev => Math.max(MIN_SCALE, Math.min(prev + delta, MAX_SCALE)));
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (scale > 1) {
        setIsDragging(true);
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
      }
    },
    [scale, position],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, dragStart],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        trackAdpEvent(
          getBernoulliAdp(),
          ADP_EVENTS.DRAIN_DETECTION_CLOSE_LIGHTBOX,
          {
            method: 'escape',
            frame: String(frameNumber),
          },
        );
        onClose();
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-') {
        handleZoomOut();
      } else if (e.key === '0') {
        handleReset();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, handleZoomIn, handleZoomOut, handleReset]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onKeyDown={e => {
        if (e.key === 'Escape') onClose();
      }}>
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black/80 backdrop-blur-sm cursor-default"
        onClick={() => {
          trackAdpEvent(
            getBernoulliAdp(),
            ADP_EVENTS.DRAIN_DETECTION_CLOSE_LIGHTBOX,
            {
              method: 'backdrop',
              frame: String(frameNumber),
            },
          );
          onClose();
        }}
        aria-label="Close lightbox"
        tabIndex={-1}
      />

      {/* Content */}
      <div className="relative w-full h-full flex flex-col pointer-events-none">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/60 to-transparent pointer-events-auto">
          <h3 className="text-white font-medium">Frame {frameNumber}</h3>
          <button
            onClick={() => {
              trackAdpEvent(
                getBernoulliAdp(),
                ADP_EVENTS.DRAIN_DETECTION_CLOSE_LIGHTBOX,
                {
                  method: 'button',
                  frame: String(frameNumber),
                },
              );
              onClose();
            }}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Close">
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Image container - interactive region for pan/zoom */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <div
          role="application"
          aria-label={`Image viewer for Frame ${frameNumber} - scroll to zoom, drag to pan`}
          className="flex-1 flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing pointer-events-auto"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}>
          <img
            src={imageUrl}
            alt={`Frame ${frameNumber}`}
            className="max-w-full max-h-full object-contain select-none"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
              transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            }}
            draggable={false}
          />
        </div>

        {/* Controls */}
        <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-center gap-2 px-4 py-4 bg-gradient-to-t from-black/60 to-transparent">
          <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
            <button
              onClick={handleZoomOut}
              disabled={scale <= MIN_SCALE}
              className="p-2 rounded-md hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Zoom out">
              <ZoomOut className="h-5 w-5 text-white" />
            </button>
            <span className="px-3 text-sm text-white font-medium min-w-[4rem] text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={handleZoomIn}
              disabled={scale >= MAX_SCALE}
              className="p-2 rounded-md hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Zoom in">
              <ZoomIn className="h-5 w-5 text-white" />
            </button>
          </div>
          <button
            onClick={handleReset}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Reset zoom">
            <RotateCcw className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Keyboard hints */}
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 text-white/50 text-xs">
          Scroll to zoom • Drag to pan • Press Esc to close
        </div>
      </div>
    </div>
  );
};

// Frame card component - compact vertical layout for grid display
const FrameCard = ({
  frameNumber,
  imageUrl,
  defects,
  timestamp,
  distance,
}: {
  frameNumber: number;
  imageUrl: string;
  defects: DrainDefect[];
  timestamp?: string | number;
  distance?: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const noDefects = hasNoDefects(defects);
  const topDefect = getTopDefect(defects);
  const formattedTimestamp = formatTimestamp(timestamp);

  return (
    <>
      {isLightboxOpen && (
        <ImageLightbox
          imageUrl={imageUrl}
          frameNumber={frameNumber}
          onClose={() => setIsLightboxOpen(false)}
        />
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm flex flex-col h-full">
        {/* Header with frame number, timestamp, and distance */}
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-[#252F33]">
            Frame {frameNumber}
          </h4>
          <div className="flex items-center gap-2">
            {formattedTimestamp && (
              <span className="text-xs text-[#768D95] font-mono flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formattedTimestamp}
              </span>
            )}
            {distance !== undefined && (
              <span className="text-xs text-[#768D95] font-mono flex items-center gap-1">
                <Ruler className="h-3 w-3" />
                {distance}m
              </span>
            )}
          </div>
        </div>

        {/* Image thumbnail - clickable with zoom icon overlay */}
        <button
          onClick={() => {
            trackAdpWaypoint(
              getBernoulliAdp(),
              ADP_EVENTS.DRAIN_DETECTION_OPEN_LIGHTBOX,
              { frame: String(frameNumber) },
            );
            setIsLightboxOpen(true);
          }}
          className="relative w-full aspect-video bg-slate-100 group cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-[#3C3C3C] focus:ring-inset">
          <img
            src={imageUrl}
            alt={`Frame ${frameNumber}`}
            className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105"
            onError={e => {
              const target = e.target as HTMLImageElement;
              target.src =
                'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text fill="%2394a3b8" font-family="sans-serif" font-size="12" x="50" y="50" text-anchor="middle" dy=".3em">No image</text></svg>';
            }}
          />
          {/* Zoom overlay on hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg">
              <Maximize2 className="h-5 w-5 text-[#252F33]" />
            </div>
          </div>
        </button>

        {/* Defect info - compact */}
        <div className="p-3 flex-1">
          <div className="flex items-start gap-2">
            {noDefects ? (
              <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
            )}
            <div className="min-w-0 flex-1">
              <span className="text-sm font-medium text-[#252F33] block truncate">
                {noDefects
                  ? 'No Defects Detected'
                  : topDefect?.defect_description || 'Defect Detected'}
              </span>
              <p className="text-xs text-[#768D95]">
                Confidence: {(topDefect?.confidence ?? 100).toFixed(0)}%
              </p>
            </div>
          </div>
        </div>

        {/* Expandable defects section */}
        <div className="border-t border-slate-100 mt-auto">
          <button
            onClick={() => {
              const next = !isExpanded;
              trackAdpEvent(
                getBernoulliAdp(),
                ADP_EVENTS.DRAIN_DETECTION_DEFECTS_TOGGLE,
                {
                  frame: String(frameNumber),
                  state: next ? 'expanded' : 'collapsed',
                },
              );
              setIsExpanded(next);
            }}
            className="w-full flex items-center justify-between px-3 py-2 text-xs text-[#43575D] hover:bg-slate-50 transition-colors">
            <span>View all defects</span>
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>

          {isExpanded && (
            <div className="px-3 pb-3 space-y-1.5">
              {defects.map((defect, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between py-1.5 px-2 bg-slate-50 rounded-lg text-xs">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    {defect.defect_code === 'None' ||
                    defect.confidence === 0 ? (
                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0" />
                    )}
                    <span className="font-medium text-[#252F33] truncate">
                      {defect.defect_code === 'None'
                        ? 'No Defect'
                        : defect.defect_code}
                    </span>
                  </div>
                  <span className="text-[#768D95] flex-shrink-0 ml-2">
                    {defect.confidence.toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export const DrainDetection = ({ data }: DrainDetectionProps) => {
  const frameCount = data.image_captions.length;

  return (
    <div className="w-full bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden shadow-lg">
      {/* Header */}
      <div className="px-5 py-4 bg-white border-b border-slate-200">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#3C3C3C] shadow-md flex-shrink-0">
            <FileVideo className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-[#252F33]">
              Drain Inspection — {data.detection_name}
            </h3>
          </div>
        </div>

        {/* Metadata - horizontal layout for wider container */}
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-[#768D95]">File:</span>
            <span className="text-[#252F33] font-medium">
              {stripUploadSuffix(data.file_name)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#768D95]">Type:</span>
            <span className="text-[#252F33] font-medium capitalize">
              {data.detection_type}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#768D95]">Status:</span>
            <StatusBadge status={data.status} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#768D95]">Updated:</span>
            <span className="text-[#252F33] font-medium flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-[#768D95]" />
              {formatDate(data.updated_at)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#768D95]">Frames:</span>
            <span className="text-[#252F33] font-medium">{frameCount}</span>
          </div>
          {data.ocr_metadata?.upstreamManholeId && (
            <div className="flex items-center gap-2">
              <span className="text-[#768D95]">Upstream Manhole:</span>
              <span className="text-[#252F33] font-medium">
                {data.ocr_metadata.upstreamManholeId}
              </span>
            </div>
          )}
          {data.ocr_metadata?.downstreamManholeId && (
            <div className="flex items-center gap-2">
              <span className="text-[#768D95]">Downstream Manhole:</span>
              <span className="text-[#252F33] font-medium">
                {data.ocr_metadata.downstreamManholeId}
              </span>
            </div>
          )}
          {data.ocr_metadata?.direction && (
            <div className="flex items-center gap-2">
              <span className="text-[#768D95]">Direction:</span>
              <span className="text-[#252F33] font-medium">
                {data.ocr_metadata.direction}
              </span>
            </div>
          )}
          {data.ocr_metadata?.surveyDateTime && (
            <div className="flex items-center gap-2">
              <span className="text-[#768D95]">Survey Date/Time:</span>
              <span className="text-[#252F33] font-medium">
                {data.ocr_metadata.surveyDateTime}
              </span>
            </div>
          )}
          {data.ocr_metadata?.pipelineRef && (
            <div className="flex items-center gap-2">
              <span className="text-[#768D95]">Pipeline Ref:</span>
              <span className="text-[#252F33] font-medium">
                {data.ocr_metadata.pipelineRef}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Frame cards - responsive grid layout */}
      <div className="p-4">
        <div
          className={`grid gap-4 ${
            frameCount === 1
              ? 'grid-cols-1 max-w-md'
              : frameCount === 2
              ? 'grid-cols-1 sm:grid-cols-2'
              : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
          }`}>
          {data.image_captions
            .sort(
              (a, b) =>
                getFrameNumber(a.image_name) - getFrameNumber(b.image_name),
            )
            .map((caption, idx) => {
              const timestampSeconds = getTimestampFromImageName(
                caption.image_name,
              );
              return (
                <FrameCard
                  key={idx}
                  frameNumber={getFrameNumber(caption.image_name) || idx + 1}
                  imageUrl={caption.image_presigned_url}
                  defects={caption.defects}
                  timestamp={timestampSeconds}
                  distance={caption.distance}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};
